/**
 * Landing Heating Calculator
 * ES module. Подключается через <script type="module" defer>.
 */

const CONSTANTS = {
  INSULATION_K: {
    well_insulated_apartment: 0.08,
    standard_apartment: 0.1,
    private_house: 0.12,
    poor_insulation: 0.15,
  },

  CORNER_SURCHARGE: 0.2,
  PANORAMIC_SURCHARGE: 0.25,

  MODE_FACTOR: {
    primary_heating: 1.0,
    additional_heating: 0.6,
  },

  IR_COMFORT: 0.95,
  BASE_CEILING: 2.5,
  PANEL_W: 500,
  OFFER_DAYS: 14,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseNum(value) {
  if (value == null) return 0;
  const n = parseFloat(String(value).replace(',', '.').trim());
  return Number.isFinite(n) ? n : 0;
}

function roundUp(n) {
  return Math.ceil(n);
}

function roundUpTo(n, decimals = 2) {
  const factor = 10 ** decimals;
  return Math.ceil(n * factor) / factor;
}

/**
 * Формула как в Calculator_ENG:
 *
 * Power W =
 * width * length * insulationWm2 * (ceiling / 2.5)
 * * (1 + cornerSurcharge)
 * * (1 + panoramicSurcharge)
 * * modeFactor
 * * IR_COMFORT
 *
 * Power kW = ROUNDUP(Power W / 1000, 2)
 * Heaters = ROUNDUP(Power W / 500, 0)
 * Daily cost = Power kW * tariff * dailyUsage
 */
function calculate(inputs) {
  const {
    width,
    length,
    ceilingHeight,
    insulation,
    heatingMode,
    cornerRoom,
    panoramicWindows,
    tariff,
    dailyUsage,
  } = inputs;

  const K = CONSTANTS.INSULATION_K[insulation] ?? CONSTANTS.INSULATION_K.standard_apartment;
  const M = CONSTANTS.MODE_FACTOR[heatingMode] ?? CONSTANTS.MODE_FACTOR.additional_heating;

  const area = width * length;
  const ceilingRatio = (ceilingHeight || CONSTANTS.BASE_CEILING) / CONSTANTS.BASE_CEILING;

  const cornerFactor = 1 + (cornerRoom ? CONSTANTS.CORNER_SURCHARGE : 0);
  const panoramicFactor = 1 + (panoramicWindows ? CONSTANTS.PANORAMIC_SURCHARGE : 0);

  const rawPowerKw =
    area * K * ceilingRatio * cornerFactor * panoramicFactor * M * CONSTANTS.IR_COMFORT;

  const powerKw = roundUpTo(rawPowerKw, 2);
  const heaters = roundUp((rawPowerKw * 1000) / CONSTANTS.PANEL_W);
  const dailyCost = Math.round(powerKw * tariff * dailyUsage * 100) / 100;

  return { powerKw, heaters, dailyCost };
}

function formatResults({ powerKw, heaters, dailyCost }) {
  return {
    power: powerKw.toFixed(2),
    heaters: String(heaters),
    cost: dailyCost.toFixed(2),
  };
}

function offerExpiryDate(days = CONSTANTS.OFFER_DAYS) {
  const d = new Date();
  d.setDate(d.getDate() + days);

  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();

  return `${dd}.${mm}.${yyyy}`;
}

function readForm(root) {
  const get = (field) => root.querySelector(`[data-calculator-field="${field}"]`);

  const checked = (field) => {
    const el = get(field);
    return el ? el.checked : false;
  };

  return {
    width: parseNum(get('width')?.value),
    length: parseNum(get('length')?.value),
    ceilingHeight: parseNum(get('ceilingHeight')?.value),
    insulation: get('insulation')?.value ?? 'standard_apartment',
    heatingMode: get('heatingMode')?.value ?? 'additional_heating',
    cornerRoom: checked('cornerRoom'),
    panoramicWindows: checked('panoramicWindows'),
    tariff: parseNum(get('tariff')?.value),
    dailyUsage: parseNum(get('dailyUsage')?.value),
  };
}

function renderResults(root, formatted) {
  const power = root.querySelector('[data-result-power]');
  const heaters = root.querySelector('[data-result-heaters]');
  const cost = root.querySelector('[data-result-cost]');

  if (power) power.textContent = formatted.power;
  if (heaters) heaters.textContent = formatted.heaters;
  if (cost) cost.textContent = formatted.cost;

  const expiry = root.querySelector('[data-offer-expiry]');
  if (expiry) expiry.textContent = offerExpiryDate();
}

function fillLeadForm(root, inputs, formatted) {
  const set = (name, value) => {
    const el = root.querySelector(`[data-lead-hidden="${name}"]`);
    if (el) el.value = value;
  };

  const insulationLabel =
    root
      .querySelector('[data-calculator-field="insulation"]')
      ?.selectedOptions?.[0]?.textContent?.trim() ?? '';

  const modeLabel =
    root
      .querySelector('[data-calculator-field="heatingMode"]')
      ?.selectedOptions?.[0]?.textContent?.trim() ?? '';

  set('room_width', inputs.width);
  set('room_length', inputs.length);
  set('ceiling_height', inputs.ceilingHeight);
  set('insulation', insulationLabel);
  set('heating_mode', modeLabel);
  set('corner_room', inputs.cornerRoom ? 'Yes' : 'No');
  set('panoramic_windows', inputs.panoramicWindows ? 'Yes' : 'No');
  set('tariff', inputs.tariff);
  set('daily_usage', inputs.dailyUsage);
  set('result_power', formatted.power);
  set('result_heaters', formatted.heaters);
  set('result_cost', formatted.cost);
}

function showFieldError(input, message) {
  input.classList.add('landing-heating-calculator__input--error');

  const err = document.createElement('p');
  err.className = 'landing-heating-calculator__field-error';
  err.textContent = message;

  input.insertAdjacentElement('afterend', err);
}

function clearFieldError(input) {
  input.classList.remove('landing-heating-calculator__input--error');

  const next = input.nextElementSibling;
  if (next?.classList.contains('landing-heating-calculator__field-error')) {
    next.remove();
  }
}

function validateLeadForm(root) {
  const form = root.querySelector('.landing-heating-calculator__lead-form');
  if (!form) return true;

  const nameInput = form.querySelector('input[name="contact[First Name]"]');
  const emailInput = form.querySelector('input[name="contact[email]"]');

  let valid = true;
  let firstInvalid = null;

  if (nameInput) clearFieldError(nameInput);
  if (emailInput) clearFieldError(emailInput);

  if (!nameInput?.value.trim()) {
    showFieldError(nameInput, 'Please enter your first name');
    if (!firstInvalid) firstInvalid = nameInput;
    valid = false;
  }

  if (!emailInput?.value.trim()) {
    showFieldError(emailInput, 'Please enter your email address');
    if (!firstInvalid) firstInvalid = emailInput;
    valid = false;
  } else if (!EMAIL_RE.test(emailInput.value.trim())) {
    showFieldError(emailInput, 'Please enter a valid email address');
    if (!firstInvalid) firstInvalid = emailInput;
    valid = false;
  }

  if (firstInvalid) firstInvalid.focus();

  return valid;
}

function createModal(root) {
  const modal = root.querySelector('[data-calculator-modal]');
  const overlay = root.querySelector('[data-modal-overlay]');
  const dialog = root.querySelector('[data-modal-dialog]');

  const showView = (view) => {
    root.querySelectorAll('[data-modal-view]').forEach((el) => {
      el.hidden = el.getAttribute('data-modal-view') !== view;
    });
  };

  let lastFocused = null;

  const open = () => {
    lastFocused = document.activeElement;

    showView('results');
    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      modal.classList.add('is-open');
      dialog?.focus();
    });
  };

  const close = () => {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';

    const onEnd = () => {
      modal.hidden = true;
      showView('results');
      modal.removeEventListener('transitionend', onEnd);
    };

    modal.addEventListener('transitionend', onEnd);

    setTimeout(() => {
      if (!modal.classList.contains('is-open')) {
        modal.hidden = true;
        showView('results');
      }
    }, 350);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  };

  overlay?.addEventListener('click', close);

  root.querySelectorAll('[data-modal-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) close();
  });

  return { open, close, showView };
}

function init() {
  document.querySelectorAll('[data-landing-heating-calculator]').forEach((root) => {
    const submitBtn = root.querySelector('[data-calculator-submit]');
    const offerBtn = root.querySelector('[data-offer-cta]');

    if (!submitBtn) return;

    const modal = createModal(root);

    let lastInputs = null;
    let lastFormatted = null;

    submitBtn.addEventListener('click', () => {
      const inputs = readForm(root);
      const result = calculate(inputs);
      const formatted = formatResults(result);

      lastInputs = inputs;
      lastFormatted = formatted;

      renderResults(root, formatted);
      modal.open();
    });

    offerBtn?.addEventListener('click', () => {
      if (lastInputs && lastFormatted) {
        fillLeadForm(root, lastInputs, lastFormatted);
      }

      modal.showView('lead');
    });

    const leadForm = root.querySelector('.landing-heating-calculator__lead-form');

    if (leadForm) {
      leadForm.noValidate = true;

      leadForm.addEventListener(
        'submit',
        (e) => {
          if (!validateLeadForm(root)) {
            e.preventDefault();
            e.stopPropagation();
          }
        },
        true,
      );

      leadForm.addEventListener('input', (e) => {
        if (e.target.matches('input')) clearFieldError(e.target);
      });
    }

    const leadSuccess = root.querySelector('[data-lead-success]');

    if (leadSuccess) {
      modal.open();
      modal.showView('lead');

      if (window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('contact_posted');
        window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export { calculate, formatResults, parseNum, offerExpiryDate, CONSTANTS };
